import {
  Autocomplete,
  Button,
  CircularProgress,
  FormControl,
  Grid2,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Box,
  Paper,
  Divider,
  Stack,
  alpha,
  useTheme,
  Skeleton,
  Chip,
} from '@mui/material';
import {
  VisibilityOutlined,
  RefreshOutlined,
  AnalyticsOutlined,
  AssignmentLateOutlined,
  FilterList,
} from '@mui/icons-material';
import { baseURL, generateFacilityReport, getToken } from '../../../../helpers/api';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { getYears } from '../../../../helpers/dateHelpers';
import { reportTypes, userCats } from '../../../../helpers/appstate';

export default function DiscussionReport({ props }) {
  const { userId, userCategory } = props;
  
  const theme = useTheme();

  const initFacility = { facilityId: '', facilityName: '' };
  const quarters = [
    { id: 1, name: 'Quarter 1' },
    { id: 2, name: 'Quarter 2' },
    { id: 3, name: 'Quarter 3' },
    { id: 4, name: 'Quarter 4' },
  ];

  const [facilityList, setFacilityList] = useState([]);
  const [facilityLoading, setFacilityLoading] = useState(false);
  const [facilitySelected, setFacilitySelected] = useState(initFacility);
  const [yearSelected, setYearSelected] = useState(new Date().getFullYear());
  const [quarterList, setQuarterList] = useState([]);
  const [quarterSelected, setQuarterSelected] = useState(2);
  const [pdfPath, setPdfPath] = useState('');
  const [reportLoadingState, setReportLoadingState] = useState('init'); //init | loading | loaded

  useEffect(() => {
    fetchFacility();
  }, []);

  useEffect(() => {
    if (facilitySelected?.facilityId) {
      fetchQuarters();
    }
  }, [facilitySelected, yearSelected]);

  const fetchFacility = async () => {
    setFacilityLoading(true);
    try {
      const url = `${baseURL}/TelerikReports/GetFacilityList/${userId}`;
      const res = await axios.get(url, {
        headers: {
          Authorization: `bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
      });
      setFacilityList(res?.data || []);
    } catch (error) {
      console.error(error);
    } finally {
      setFacilityLoading(false);
    }
  };

  const fetchQuarters = async () => {
    try {
      const res = await generateFacilityReport(facilitySelected?.facilityId, yearSelected);
      const { data, status } = res?.result || {};
      if (status?.code === '00') {
        setQuarterList(data || []);
      }
    } catch (error) {
      console.error('Error fetching quarters:', error);
    }
  };

  const handleReportAction = async (actionType) => {
    setReportLoadingState('loading');
    try {
      const endpoint = actionType === 'preview' ? 'PreviewIemsReport' : 'GenerateIemsReport';
      const url = `${baseURL}/Report/${endpoint}/${facilitySelected?.facilityId}/${quarterSelected}/${yearSelected}/ReportGroupId?ReportGroupId=1`;
      const res = await axios.patch(
        url,
        {},
        {
          headers: { Authorization: `bearer ${getToken()}`, 'Content-Type': 'application/json' },
        }
      );
      setPdfPath(res?.data?.result?.data);
    } catch (error) {
      console.error(error);
    } finally {
      setReportLoadingState('loaded');
    }
  };

  const handleRenderContent = () => {
    if (reportLoadingState === 'init') {
      return (
        <Stack
          spacing={2}
          alignItems="center"
          justifyContent="center"
          sx={{ height: '100%', opacity: 0.6 }}
        >
          <AnalyticsOutlined sx={{ fontSize: 80, color: 'primary.main' }} />
          <Typography variant="h6" fontWeight={500}>
            Ready to Generate Report
          </Typography>
          <Typography variant="body2">Fill in the parameters on the right to begin</Typography>
        </Stack>
      );
    }

    if (reportLoadingState === 'loading') {
      return (
        <Box sx={{ width: '100%', height: '100%', p: 4 }}>
          <Stack spacing={2}>
            <Skeleton variant="rectangular" width="100%" height={60} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rectangular" width="100%" height="80%" sx={{ borderRadius: 2 }} />
          </Stack>
        </Box>
      );
    }

    if (pdfPath) {
      return (
        <Box sx={{ height: '100%', width: '100%', overflow: 'hidden' }}>
          {/* Native Browser PDF Viewer will show its own toolbar (Zoom, Download, Print) */}
          <iframe
            src={pdfPath}
            width="100%"
            height="100%"
            style={{ border: 'none' }}
            title="PDF Preview"
          />
        </Box>
      );
    }

    return (
      <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
        <AssignmentLateOutlined sx={{ fontSize: 80, color: 'error.light' }} />
        <Typography variant="h6">No Report Found</Typography>
        <Typography color="textSecondary">Try regenerating the report for this period</Typography>
      </Stack>
    );
  };

  return (
    <Box sx={{ height: '80vh', bgcolor: 'background.default', overflow: 'hidden' }}>
      <Grid2 container sx={{ height: '100%' }} spacing={0}>
        {/* Main Preview Panel */}
        <Grid2 size={{ lg: 9, md: 8, sm: 12, xs: 12 }} sx={{ height: '100%' }}>
          <Box sx={{ height: '100%', borderRight: 1, borderColor: 'divider',}}>
            <Paper
              variant="outlined"
              sx={{
                height: '100%',
                //borderRadius: 2,
                overflow: 'hidden',
                bgcolor: 'background.paper',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{ p: 1, px: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}
              >
                <Typography variant="subtitle2" fontWeight={700}>
                  Document Preview
                </Typography>
              </Box>
              <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>{handleRenderContent()}</Box>
            </Paper>
          </Box>
        </Grid2>

        {/* Sidebar Controls */}
        <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }} sx={{ height: '100%', bgcolor: 'white' }}>
          <Stack sx={{ height: '100%' }}>
            <Box
              sx={{
                p: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <FilterList color="primary" fontSize="small" />
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  color="primary.main"
                  sx={{ textTransform: 'uppercase' }}
                >
                  Preview Controls
                </Typography>
              </Stack>
            </Box>

            <Box sx={{ p: 3, flexGrow: 1, overflowY: 'auto' }}>
              <Stack spacing={3}>
                <Autocomplete
                  fullWidth
                  size="small"
                  options={facilityList}
                  getOptionLabel={(option) => option.facilityName || ''}
                  value={facilitySelected}
                  onChange={(_, value) => value && setFacilitySelected(value)}
                  renderInput={(params) => (
                    <TextField
                      size="small"
                      {...params}
                      label="Facility"
                      required
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: facilityLoading && (
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                        ),
                      }}
                    />
                  )}
                />

                <FormControl fullWidth size="small">
                  <InputLabel>Year *</InputLabel>
                  <Select
                    size="small"
                    label="Year *"
                    value={yearSelected}
                    onChange={(e) => setYearSelected(e.target.value)}
                  >
                    {getYears().map((y) => (
                      <MenuItem key={y} value={y}>
                        {y}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel>Quarter *</InputLabel>
                  <Select
                    label="Quarter *"
                    size="small"
                    value={quarterSelected}
                    onChange={(e) => setQuarterSelected(e.target.value)}
                  >
                    {quarterList?.map((y, index) => {
                      const isEdm = userCategory === userCats.EDM;
                      const isQaqc = y?.reportType === reportTypes.QAQC;

                      return (
                        <MenuItem key={index} value={y?.reportQuarter} disabled={isQaqc && !isEdm}>
                          Quarter {y?.reportQuarter}{' '}
                          {isQaqc && (
                            <Chip
                              label={y?.reportType}
                              size="small"
                              color={isEdm ? 'warning' : 'default'}
                              sx={{ ml: 1, fontSize: '0.8rem' }}
                            />
                          )}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Stack>
            </Box>

            <Divider />

            <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Stack spacing={1.5}>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  startIcon={
                    reportLoadingState === 'loading' ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <VisibilityOutlined />
                    )
                  }
                  disabled={reportLoadingState === 'loading'}
                  onClick={() => handleReportAction('preview')}
                  sx={{ py: 1.2, textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
                >
                  Preview
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  startIcon={
                    reportLoadingState === 'loading' ? (
                      <CircularProgress size={20} />
                    ) : (
                      <RefreshOutlined />
                    )
                  }
                  disabled={reportLoadingState === 'loading'}
                  onClick={() => handleReportAction('regenerate')}
                  sx={{ py: 1.2, textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
                >
                  Regenerate
                </Button>
              </Stack>
            </Box>
          </Stack>
        </Grid2>
      </Grid2>
    </Box>
  );
}
